import React from 'react';
import { IonApp, IonContent, IonPage } from '@ionic/react';
import { Helmet } from 'react-helmet-async';
import Header from 'layouts/Header';
import './PrivacyPage.css';

const PrivacyPage = () => {
  return (
    <IonApp>
      <Helmet>
        <title>Privacy Policy - Teamy</title>
        <meta name="description" content="Teamy Privacy Policy - Learn how we collect, use, and protect your personal information when using our AI-powered software testing and development platform." />
      </Helmet>
      <IonPage>
        <Header user={null} />
        <IonContent className="privacy-ion-content">
          <div className="privacy-page">
      <div className="privacy-container">
        <header className="privacy-header">
          <h1>Privacy Policy</h1>
          <p className="last-updated">Last Updated: August 6, 2026</p>
        </header>

        <div className="privacy-content">
          <section className="privacy-section">
            <h2>1. Introduction</h2>
            <p>
              This Privacy Policy explains how Zibby Labs ("Teamy," "we," "our," or "us") collects, uses, discloses, 
              and protects your personal information when you use our AI-powered software testing and development platform 
              and related services (the "Service"). We are committed to protecting your privacy and handling your data 
              responsibly.
            </p>
            <p>
              By using the Service, you agree to the collection and use of information in accordance with this policy 
              and our <a href="/terms">Terms of Service</a>. If you do not agree with our policies, please do not use the Service.
            </p>
          </section>

          <section className="privacy-section">
            <h2>2. Information We Collect</h2>
            
            <h3>Information You Provide Directly</h3>
            <ul>
              <li><strong>Account Information:</strong> Name, email address, and authentication credentials when you sign in through OAuth providers (Google, Microsoft, Facebook, Apple)</li>
              <li><strong>Profile Information:</strong> Profile details, preferences, and team/organization information you provide</li>
              <li><strong>Project Data:</strong> Project configurations, ticket information, test cases, workflow definitions, and other content you create or import through the Service</li>
              <li><strong>Integration Credentials:</strong> API tokens and connection details for third-party services you connect (e.g. Jira, GitHub, GitLab)</li>
              <li><strong>Source Code and Repositories:</strong> Code repositories, branches, and pull requests you authorize us to access for implementation and analysis features</li>
              <li><strong>Payment Information:</strong> Billing details provided through our payment processor (Stripe); we do not directly store your full credit card number</li>
              <li><strong>Support Communications:</strong> Information you provide when contacting support or submitting feedback</li>
            </ul>

            <h3>Information Collected Automatically</h3>
            <ul>
              <li><strong>Usage Data:</strong> Features used, actions taken, session duration, test execution logs, and interaction patterns within the Service</li>
              <li><strong>Device Information:</strong> Device type, operating system, browser type, screen resolution, and unique device identifiers</li>
              <li><strong>Log Data:</strong> Server logs, error reports, API request logs, and performance metrics</li>
              <li><strong>IP Address:</strong> Used for security, fraud prevention, and approximate geographic location</li>
            </ul>

            <h3>Information from Third-Party Services</h3>
            <ul>
              <li><strong>OAuth Providers:</strong> Basic profile information from your sign-in provider (name, email, profile picture)</li>
              <li><strong>Google Workspace (Docs / Drive):</strong> If you connect Google, the content of Google Drive files you explicitly select and (optionally) the Google Docs you point Teamy at — used only to power the feature you request. See "Google User Data &amp; Limited Use" below.</li>
              <li><strong>Project Management Tools:</strong> Ticket data, sprint information, and project metadata from connected tools like Jira or Linear</li>
              <li><strong>Source Control Platforms:</strong> Repository metadata, code content, pull request information, and commit history from platforms like GitHub, GitLab, or Bitbucket</li>
              <li><strong>Slack:</strong> If you install the Teamy app into a Slack workspace, the messages addressed to it and the related workspace data described in "Slack App and Slack Workspace Data" below</li>
            </ul>
          </section>

          <section className="privacy-section">
            <h2>3. How We Use Your Information</h2>
            
            <h3>Core Service Delivery</h3>
            <ul>
              <li><strong>AI Analysis:</strong> Process your tickets, code, and project data to generate test cases, code implementations, and analysis reports</li>
              <li><strong>Test Execution:</strong> Run automated tests against your applications and generate execution reports</li>
              <li><strong>Workflow Automation:</strong> Execute configured workflows and integrations on your behalf</li>
              <li><strong>Account Management:</strong> Create and maintain your account, manage team access, and process your subscription</li>
              <li><strong>Notifications:</strong> Send service updates, execution results, and security alerts</li>
            </ul>

            <h3>Service Improvement</h3>
            <ul>
              <li><strong>Product Development:</strong> Analyze usage patterns to improve features and user experience</li>
              <li><strong>AI Model Improvement:</strong> Use aggregated and anonymized usage metrics (such as which features are used and how often) to improve how our agents are built and prompted. We never use your content &mdash; your code, your messages, your Slack or chat data, or your connected-service data &mdash; to train language models, and neither do our AI providers</li>
              <li><strong>Performance Optimization:</strong> Monitor and improve Service reliability and performance</li>
              <li><strong>Security:</strong> Detect, prevent, and respond to fraud, abuse, and security incidents</li>
            </ul>

            <h3>Legal and Business</h3>
            <ul>
              <li><strong>Compliance:</strong> Comply with applicable laws, regulations, and legal processes</li>
              <li><strong>Business Operations:</strong> Conduct analytics, billing, and business planning</li>
            </ul>
          </section>

          <section className="privacy-section">
            <h2>4. AI Processing and Your Data</h2>
            <p>
              Our Service uses artificial intelligence to analyze your project data and generate outputs. 
              Here is how we handle AI processing:
            </p>
            <ul>
              <li><strong>Ticket Analysis:</strong> Your ticket descriptions and acceptance criteria are processed by AI models to generate test cases and analysis</li>
              <li><strong>Code Generation:</strong> Source code and project context are processed to generate implementations and pull requests</li>
              <li><strong>Test Generation:</strong> Application context and requirements are used to create and execute automated tests</li>
              <li><strong>Third-Party AI Providers:</strong> We use Anthropic, OpenAI, and other AI service providers to power certain features; data sent to these providers is subject to their respective data processing agreements</li>
              <li><strong>Data Isolation:</strong> Your project data is logically isolated from other customers' data</li>
              <li><strong>No Training on Your Data:</strong> We do not use your proprietary code or project data to train general-purpose AI models shared with other customers</li>
            </ul>
          </section>

          <section className="privacy-section">
            <h2>5. Slack App and Slack Workspace Data</h2>
            <p>
              If you install the Teamy app into a Slack workspace, or connect Slack from your Teamy Project Settings,
              Teamy processes data from that workspace as described in this section. This section applies in addition
              to the rest of this policy and describes all Slack data Teamy receives — including data we receive but
              do not use.
            </p>

            <h3>Slack Data We Receive</h3>
            <ul>
              <li><strong>Messages Addressed to Teamy:</strong> An @mention of the Teamy app in a channel, a direct message to it, or a reply in a thread it is already participating in. Messages that are not addressed to Teamy are ignored and are not processed.</li>
              <li><strong>Message Metadata:</strong> The workspace/team ID, channel ID, thread timestamp, and the Slack user ID of the sender.</li>
              <li><strong>Sender Email Address:</strong> The sender's workspace-verified email address, read at request time from Slack (<code>users.info</code>) solely to match the sender to their existing Teamy account. It is used only for that lookup and is not stored.</li>
              <li><strong>Images and Files You Send:</strong> When you send an image or file to Teamy, the file is downloaded to answer that request. We retain only a pointer to it (its kind, the provider, and a reference to the message) — never the file bytes.</li>
              <li><strong>Workspace Directory Metadata:</strong> Lists of public and private channels the app can see, workspace user profiles, and user groups, read on demand only to fulfil a request you make. These results are not retained.</li>
              <li><strong>OAuth Bot Token:</strong> The token issued when the app is installed, stored encrypted.</li>
            </ul>

            <h3>What We Store, and For How Long</h3>
            <ul>
              <li><strong>Conversation History:</strong> Your message text and Teamy's reply text, per turn, keyed by thread. Retained for <strong>90 days</strong>, enforced by an automatic time-to-live. Tool results, and the surrounding channel or thread messages Teamy reads for context, are not stored.</li>
              <li><strong>Attachment Pointers:</strong> Stored alongside the conversation history under the same <strong>90-day</strong> retention. No file bytes are stored.</li>
              <li><strong>Duplicate-Delivery Markers:</strong> Retained for <strong>24 hours</strong> so a message re-delivered by Slack is not answered twice.</li>
              <li><strong>Thread-Participation Markers:</strong> Retained for <strong>90 days</strong> so Teamy knows it may continue a thread it is already part of.</li>
              <li><strong>Monthly Active-User Metering:</strong> One record per distinct Slack user per month, containing the workspace ID, the sender's Slack user ID, the month, and message counts. Used for billing and product metrics.</li>
              <li><strong>Remembered Facts:</strong> A conservative heuristic extracts durable facts from conversations (for example a stated preference or a project name) and stores them per user so Teamy can recall them in later conversations. <strong>By default these have no expiry</strong> — unlike the items above they are not deleted on a timer, and they are kept until you ask us to delete them as described below.</li>
            </ul>

            <h3>How Slack Data Is Used</h3>
            <ul>
              <li><strong>Generating Replies:</strong> The content of messages addressed to Teamy is sent to the large language model provider that powers the assistant — by default <strong>Anthropic (Claude)</strong> — to generate a reply. Which provider is used depends on the model credential the customer supplies.</li>
              <li><strong>No LLM Training:</strong> Slack data is <strong>not</strong> used to train language models. Our AI providers process it under data processing terms and do not train on it.</li>
            </ul>

            <h3>Deletion and Control</h3>
            <ul>
              <li><strong>Removing the App:</strong> Removing the Teamy app from your Slack workspace, or disconnecting Slack in your Teamy Project Settings, stops all further processing and deletes the stored bot token.</li>
              <li><strong>Access, Export, and Deletion:</strong> You can request access to, an export of, or deletion of your Slack-derived data — including remembered facts — by emailing <a href="mailto:support@teamy.town">support@teamy.town</a>. We process deletion requests within 30 days, subject to any legal retention requirements.</li>
            </ul>
          </section>

          <section className="privacy-section">
            <h2>6. Information Sharing and Disclosure</h2>
            
            <h3>We Share Information With:</h3>
            <ul>
              <li><strong>Your Team Members:</strong> Other users within your organization or projects who have been granted access</li>
              <li><strong>Service Providers:</strong> Cloud infrastructure providers (AWS), analytics platforms, and other vendors that help us operate the Service, subject to data processing agreements</li>
              <li><strong>AI Service Providers:</strong> Anthropic, OpenAI, and other AI platforms that process data to provide AI-powered features, under strict data processing terms</li>
              <li><strong>Integrated Services:</strong> Third-party tools you connect (Jira, GitHub, etc.) to the extent necessary for the integration to function</li>
              <li><strong>Payment Processor:</strong> Stripe processes your payment information in accordance with PCI DSS standards</li>
              <li><strong>Legal Requirements:</strong> Law enforcement, government agencies, or other parties when required by law, court order, or to protect our rights and safety</li>
              <li><strong>Business Transfers:</strong> In connection with mergers, acquisitions, bankruptcy, or asset sales, with appropriate notice to you</li>
            </ul>

            <h3>We Do Not:</h3>
            <ul>
              <li>Sell your personal information to third parties</li>
              <li>Share your proprietary code or project data for marketing or advertising purposes</li>
              <li>Use your data to train AI models that benefit other customers</li>
              <li>Provide your data to advertisers</li>
            </ul>
          </section>

          <section className="privacy-section">
            <h2>7. Data Security</h2>
            <p>
              We implement industry-standard technical and organizational security measures to protect your information:
            </p>
            <ul>
              <li><strong>Encryption:</strong> All data is encrypted in transit (TLS 1.2+) and at rest (AES-256)</li>
              <li><strong>Access Controls:</strong> Role-based access controls, multi-factor authentication support, and least-privilege access for our team</li>
              <li><strong>Infrastructure Security:</strong> Hosted on AWS with enterprise-grade security controls, including network isolation, firewalls, and DDoS protection</li>
              <li><strong>Monitoring:</strong> Continuous security monitoring, logging, and anomaly detection</li>
              <li><strong>Incident Response:</strong> Documented incident response procedures and breach notification processes</li>
            </ul>
            <p>
              No method of transmission over the internet or electronic storage is 100% secure. While we implement 
              robust security measures, we cannot guarantee absolute security of your data.
            </p>
          </section>

          <section className="privacy-section">
            <h2>8. Data Retention</h2>
            <p>We retain your information as follows:</p>
            <ul>
              <li><strong>Account Data:</strong> Retained for the duration of your account and up to 30 days after account deletion</li>
              <li><strong>Project Data:</strong> Retained while your account is active; available for export upon request before deletion</li>
              <li><strong>Execution Logs:</strong> Test execution data and logs are retained in accordance with your subscription plan</li>
              <li><strong>Slack Assistant Data:</strong> Per-item retention periods for Slack conversation history, attachment pointers, delivery and thread markers, metering records, and remembered facts are set out in Section 5 ("Slack App and Slack Workspace Data") above</li>
              <li><strong>Usage Analytics:</strong> Aggregated and anonymized analytics data may be retained indefinitely for service improvement</li>
              <li><strong>Legal Requirements:</strong> Certain data may be retained longer when required by applicable laws or regulations</li>
            </ul>
            <p>
              You can request deletion of your account and associated data at any time by contacting us at support@teamy.town. 
              We will process deletion requests within 30 days, subject to any legal retention requirements.
            </p>
          </section>

          <section className="privacy-section">
            <h2>9. Your Privacy Rights</h2>
            
            <h3>You Have the Right To:</h3>
            <ul>
              <li><strong>Access:</strong> Request a copy of the personal information we hold about you</li>
              <li><strong>Correction:</strong> Request correction of inaccurate or incomplete personal information</li>
              <li><strong>Deletion:</strong> Request deletion of your personal information, subject to legal retention obligations</li>
              <li><strong>Data Portability:</strong> Request your data in a structured, commonly used, machine-readable format</li>
              <li><strong>Restriction:</strong> Request limitation of processing of your personal information in certain circumstances</li>
              <li><strong>Objection:</strong> Object to processing based on legitimate interests</li>
              <li><strong>Withdraw Consent:</strong> Withdraw consent at any time where consent is the legal basis for processing</li>
            </ul>

            <h3>How to Exercise Your Rights</h3>
            <p>
              To exercise any of these rights, contact us at support@teamy.town or through your account settings. 
              We will respond to verified requests within 30 days. We may need to verify your identity before 
              processing certain requests.
            </p>
          </section>

          <section className="privacy-section">
            <h2>10. Third-Party Services</h2>
            <p>
              The Service integrates with various third-party platforms. When you connect these services, 
              their privacy policies also apply to the data they process:
            </p>
            <ul>
              <li><strong>Google:</strong> <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Google Privacy Policy</a></li>
              <li><strong>Microsoft:</strong> <a href="https://privacy.microsoft.com/privacystatement" target="_blank" rel="noopener noreferrer">Microsoft Privacy Statement</a></li>
              <li><strong>Atlassian (Jira):</strong> <a href="https://www.atlassian.com/legal/privacy-policy" target="_blank" rel="noopener noreferrer">Atlassian Privacy Policy</a></li>
              <li><strong>GitHub:</strong> <a href="https://docs.github.com/en/site-policy/privacy-policies/github-general-privacy-statement" target="_blank" rel="noopener noreferrer">GitHub Privacy Statement</a></li>
              <li><strong>Slack:</strong> <a href="https://slack.com/trust/privacy/privacy-policy" target="_blank" rel="noopener noreferrer">Slack Privacy Policy</a></li>
              <li><strong>Anthropic:</strong> <a href="https://www.anthropic.com/legal/privacy" target="_blank" rel="noopener noreferrer">Anthropic Privacy Policy</a></li>
              <li><strong>OpenAI:</strong> <a href="https://openai.com/privacy/" target="_blank" rel="noopener noreferrer">OpenAI Privacy Policy</a></li>
              <li><strong>Stripe:</strong> <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer">Stripe Privacy Policy</a></li>
            </ul>

            <h3 id="limited-use">Google User Data &amp; Limited Use</h3>
            <p>
              When you sign in with Google or connect Google Workspace features, Teamy accesses a
              limited set of Google user data, strictly to provide the features you request:
            </p>
            <ul>
              <li><strong>Sign-in (email &amp; profile):</strong> Your name, email address, and profile picture
                (scopes <code>userinfo.email</code>, <code>userinfo.profile</code>) — used only to create and
                authenticate your account.</li>
              <li><strong>Google Drive &amp; Docs files you select:</strong> Files you explicitly create through
                Teamy, or open by picking them in the Google Picker (scope <code>drive.file</code>) — used only to
                read or write those specific documents for the feature you invoked. Teamy has no access to any
                Drive/Docs file you have not created or picked; we do not access your other Drive files.</li>
            </ul>
            <p>
              <strong>Limited Use.</strong> Teamy's use and transfer to any other app of information received
              from Google APIs will adhere to the{' '}
              <a href="https://developers.google.com/terms/api-services-user-data-policy" target="_blank" rel="noopener noreferrer">Google API Services User Data Policy</a>,
              including the Limited Use requirements. In particular, we do <strong>not</strong> use Google user
              data for advertising, we do <strong>not</strong> sell it, and we do <strong>not</strong> use it to
              train generalized or shared AI models. Humans do not read your Google data except with your
              consent, for security or abuse investigations, to comply with applicable law, or where strictly
              necessary to provide the feature you requested (and where feasible, on aggregated or anonymized
              data). You can revoke Teamy's access at any time from your{' '}
              <a href="https://myaccount.google.com/permissions" target="_blank" rel="noopener noreferrer">Google Account permissions</a>{' '}
              or by disconnecting the integration inside Teamy, and you may request deletion of the associated
              data as described in "Data Retention" and "Your Privacy Rights" above.
            </p>
          </section>

          <section className="privacy-section">
            <h2>11. Cookies and Tracking</h2>
            <p>We use cookies and similar technologies for:</p>
            <ul>
              <li><strong>Essential Cookies:</strong> Required for authentication, session management, and core Service functionality</li>
              <li><strong>Preference Cookies:</strong> Remember your settings and preferences</li>
              <li><strong>Analytics Cookies:</strong> Help us understand how the Service is used so we can improve it</li>
            </ul>
            <p>
              You can manage cookie preferences through your browser settings. Disabling essential cookies 
              may affect the functionality of the Service.
            </p>
          </section>

          <section className="privacy-section">
            <h2>12. International Data Transfers</h2>
            <p>
              Our Service is hosted on AWS infrastructure primarily in the Asia-Pacific (Sydney) region. 
              Your information may be transferred to and processed in countries other than your country of residence, 
              including Australia and the United States (for certain AI processing). We ensure appropriate 
              safeguards are in place for international transfers, including:
            </p>
            <ul>
              <li>Standard Contractual Clauses approved by relevant regulatory authorities</li>
              <li>Data processing agreements with all sub-processors</li>
              <li>Compliance with applicable data transfer frameworks</li>
            </ul>
          </section>

          <section className="privacy-section">
            <h2>13. Children's Privacy</h2>
            <p>
              The Service is designed for professional use and is not intended for children under 16 years of age. 
              We do not knowingly collect personal information from children under 16. If we become aware that 
              we have collected personal information from a child under 16, we will take prompt steps to delete 
              such information.
            </p>
          </section>

          <section className="privacy-section">
            <h2>14. Changes to This Privacy Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify you of material changes by:
            </p>
            <ul>
              <li>Posting the revised policy on this page with an updated "Last Updated" date</li>
              <li>Sending an email notification to the address associated with your account</li>
              <li>Displaying a prominent notice within the Service</li>
            </ul>
            <p>
              Changes become effective when posted unless otherwise stated. Your continued use of the Service 
              after changes take effect constitutes acceptance of the updated policy.
            </p>
          </section>

          <section className="privacy-section">
            <h2>15. Region-Specific Disclosures</h2>
            
            <h3>Australian Privacy Act</h3>
            <p>
              We comply with the Australian Privacy Principles (APPs) under the Privacy Act 1988 (Cth). 
              Australian residents may lodge a complaint with us or with the Office of the Australian 
              Information Commissioner (OAIC) if they believe their privacy has been breached.
            </p>

            <h3>European Union Residents (GDPR)</h3>
            <p>
              EU/EEA residents have additional rights under the General Data Protection Regulation (GDPR). 
              Our lawful bases for processing include consent, contract performance, legitimate interests, 
              and legal compliance. You may lodge a complaint with your local Data Protection Authority.
            </p>

            <h3>California Residents (CCPA/CPRA)</h3>
            <p>
              California residents have rights under the California Consumer Privacy Act and the California Privacy Rights Act, 
              including the right to know, delete, correct, and opt-out of the sale or sharing of personal information. 
              We do not sell your personal information.
            </p>
          </section>

          <section className="privacy-section">
            <h2>16. Contact Us</h2>
            <p>
              If you have any questions about this Privacy Policy, our privacy practices, or wish to exercise 
              your privacy rights, please contact us:
            </p>
            <div className="contact-info">
              <p><strong>Email:</strong> support@teamy.town</p>
              <p><strong>Website:</strong> <a href="https://zibby.dev">https://zibby.dev</a></p>
            </div>
          </section>
          </div>
        </div>
      </div>
        </IonContent>
      </IonPage>
    </IonApp>
  );
};

export default PrivacyPage;
